/**
 * Pool API for web mode
 */

import { httpClient } from './httpClient'
import type { ApiResponse } from '../types'
import type { components } from '../../../../../types/flex-api'

type Pool = components['schemas']['Pool']

/**
 * Get all pools
 */
export async function getAll(): Promise<ApiResponse<Pool[]>> {
  try {
    const response = await httpClient.getClient().get<{ pools: Pool[] }>('/pools')
    return {
      success: true,
      data: response.data.pools || [],
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get pools',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Get a single pool by ID
 */
export async function getById(poolID: string): Promise<ApiResponse<Pool>> {
  try {
    const response = await httpClient.getClient().get<Pool>(`/pools/${poolID}`)
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get pool',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Create a new pool
 */
export async function create(data: {
  accountID: string
  name: string
  isDefault?: boolean
  controlConfig?: {
    purpose: 'imbalance' | 'tou_stacking_poc' | 'intraday' | 'intraday_cloud'
    dryRun?: boolean
    multiTargetAsset?: boolean
    fallbackBehavior?: 'none' | 'last_possible_moment' | 'time_of_use'
  }
  forecastConfigs?: Array<{
    purpose: 'gcp_supply' | 'gcp_feed_in' | 'gcp_without_ev' | 'ev'
    isActive?: boolean
  }>
  tags?: Record<string, string>
}): Promise<ApiResponse<Pool>> {
  try {
    const response = await httpClient.getClient().post<Pool>('/pools', data)
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to create pool',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Update pool name, tags, or other patchable fields
 */
export async function update(
  poolID: string,
  data: {
    name?: string
    isDefault?: boolean
    tags?: Record<string, string | null>
    evChargingEfficiencyFactor?: number | null
    controlConfig?: {
      dryRun?: boolean
      fallbackBehavior?: 'none' | 'last_possible_moment' | 'time_of_use'
    }
    forecastConfigs?: Array<{
      purpose: 'gcp_supply' | 'gcp_feed_in' | 'gcp_without_ev' | 'ev'
      isActive?: boolean
    }> | null
  }
): Promise<ApiResponse<Pool>> {
  try {
    const response = await httpClient.getClient().patch<Pool>(`/pools/${poolID}`, data)
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to update pool',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Delete a pool
 */
export async function deletePool(poolID: string): Promise<ApiResponse<void>> {
  try {
    await httpClient.getClient().delete(`/pools/${poolID}`)
    return {
      success: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to delete pool',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}

/**
 * Get pool composition at a specific point in time
 */
export async function getHistory(
  poolID: string,
  timestamp: string
): Promise<ApiResponse<{ systemIDs: string[]; timestamp: string }>> {
  try {
    const response = await httpClient.getClient().get<{
      systemIDs: string[]
      timestamp: string
    }>(`/pools/${poolID}/history`, {
      params: { timestamp },
    })
    return {
      success: true,
      data: response.data,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get pool history',
      errorCode: error.code || 'UNKNOWN_ERROR',
      details: error.details,
    }
  }
}
